import { useState } from 'react';
import { C, SERIF, mono } from '../../theme';
import { Icon } from '../../icons';
import { Loading, Empty } from '../../components/States';
import { useToast } from '../../components/Toast';
import { api, ApiError } from '../../api';
import type { CriticalDateRecord, TenancyRow } from '../../api';
import { useAsync } from '../../useAsync';
import { fmtDate } from '../../format';

const DATE_TYPES = ['renewal', 'escalation', 'insurance', 'registration', 'notice_window', 'lock_in', 'rent_due', 'custom'] as const;

function typeLabel(t: string): string {
  return t.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Render a lead-time schedule "90,60,30" as small day chips. */
function LeadChips({ leadDays }: { leadDays?: string | null }) {
  const days = (leadDays ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  if (days.length === 0) return null;
  return (
    <span style={{ display: 'inline-flex', gap: 4 }}>
      {days.map((d) => (
        <span key={d} style={{ fontSize: 10.5, fontWeight: 600, color: C.muted, background: C.soft, borderRadius: 999, padding: '2px 7px' }}>{d}d</span>
      ))}
    </span>
  );
}

/** Days from now → { n, unit, over }. */
function countdown(dueDate?: number | null): { n: string; unit: string; over: boolean } {
  if (dueDate == null) return { n: '–', unit: '', over: false };
  const days = Math.round((dueDate - Date.now()) / 86_400_000);
  if (days < 0) return { n: String(Math.abs(days)), unit: `${Math.abs(days) === 1 ? 'day' : 'days'} over`, over: true };
  if (days === 0) return { n: 'today', unit: '', over: true };
  return { n: String(days), unit: days === 1 ? 'day' : 'days', over: false };
}

interface RemData {
  dates: CriticalDateRecord[];
  tenancies: TenancyRow[];
}

/**
 * Reminders — the property's critical-date radar: renewals, escalations,
 * insurance and licence expiries. Upcoming and overdue sit up top sorted by
 * due date; completed ones drop to a quiet history. Add or tick them off here.
 */
export function RemindersPage({ propertyId, propertyName }: { propertyId?: string; propertyName?: string }) {
  const notify = useToast();
  const [showAdd, setShowAdd] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);

  const { data, loading, error, reload } = useAsync<RemData>(async () => {
    const [dates, tenancies] = await Promise.all([
      api.criticalDates(),
      api.tenancies().catch(() => [] as TenancyRow[]),
    ]);
    return { dates, tenancies };
  }, [propertyId]);

  const tenancyName = (id?: string | null) => {
    if (!id) return null;
    const t = data?.tenancies.find((x) => x.id === id);
    return t ? `${t.unitLabel} · ${t.tenantName}` : null;
  };

  const active = (data?.dates ?? [])
    .filter((d) => d.status === 'upcoming')
    .sort((a, b) => (a.dueDate ?? Infinity) - (b.dueDate ?? Infinity));
  const done = (data?.dates ?? []).filter((d) => d.status === 'done');

  async function setStatus(id: string, status: 'done' | 'dismissed') {
    setBusyId(id);
    try {
      await api.updateCriticalDate(id, { status });
      notify(status === 'done' ? 'Marked done' : 'Dismissed');
      reload();
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'Could not update');
    } finally {
      setBusyId(null);
    }
  }

  /** Regenerate lease-derived dates (renewal, notice, lock-in, escalation) across every tenancy. */
  async function syncFromLease() {
    const tens = data?.tenancies ?? [];
    if (tens.length === 0) {
      notify('Add a tenancy first.');
      return;
    }
    setSyncing(true);
    try {
      const results = await Promise.all(tens.map((t) => api.deriveTenancyDates(t.id).catch(() => ({ created: 0 }))));
      const made = results.reduce((s, r) => s + (r.created ?? 0), 0);
      notify(made > 0 ? `Added ${made} date${made === 1 ? '' : 's'} from leases` : 'Lease dates already up to date');
      reload();
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'Could not sync from leases');
    } finally {
      setSyncing(false);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ fontFamily: SERIF, fontSize: 22, margin: '0 0 3px', fontWeight: 600 }}>Reminders</h2>
          <div style={{ color: C.muted, fontSize: 13.5 }}>
            {propertyName ?? 'Property'}
            {data ? ` · ${active.length} upcoming` : ''}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={syncFromLease} disabled={syncing} style={{ ...ghostBtn, padding: '9px 13px', opacity: syncing ? 0.6 : 1 }}>
            <Icon n="bell" size={14} /> {syncing ? 'Syncing…' : 'Sync from lease'}
          </button>
          <button onClick={() => setShowAdd((s) => !s)} style={primaryBtn}>
            <Icon n={showAdd ? 'chev' : 'bell'} size={15} color="#fff" style={showAdd ? { transform: 'rotate(-90deg)' } : undefined} />
            {showAdd ? 'Close' : 'Add reminder'}
          </button>
        </div>
      </div>

      {showAdd && (
        <AddPanel
          propertyId={propertyId}
          tenancies={data?.tenancies ?? []}
          onDone={() => { setShowAdd(false); reload(); }}
          notify={notify}
        />
      )}

      {loading ? (
        <Loading label="Loading reminders…" minHeight={340} />
      ) : error ? (
        <Empty icon="bell" title="Reminders unavailable" hint="We couldn't reach the date radar." minHeight={340}>
          <button onClick={reload} style={retryBtn}>Retry</button>
        </Empty>
      ) : active.length === 0 && done.length === 0 ? (
        <Empty icon="bell" title="Nothing on the radar" hint="Add a renewal, insurance or licence-expiry date and Premise will keep it in view." minHeight={300}>
          <button onClick={() => setShowAdd(true)} style={retryBtn}>Add a reminder</button>
        </Empty>
      ) : (
        <>
          {active.length === 0 ? (
            <div style={{ background: C.greenSoft, border: `1px solid #cfe4da`, borderRadius: 13, padding: '16px 18px', display: 'flex', alignItems: 'center', gap: 12, color: C.hi }}>
              <span style={{ width: 34, height: 34, borderRadius: 999, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}><Icon n="check" size={17} /></span>
              <div style={{ fontSize: 13.8, fontWeight: 600 }}>You're all clear. No upcoming or overdue dates.</div>
            </div>
          ) : (
            <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 13, overflow: 'hidden' }}>
              {active.map((d, i) => {
                const c = countdown(d.dueDate);
                const scope = tenancyName(d.tenancyId);
                return (
                  <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderTop: i ? `1px solid ${C.line2}` : 0 }}>
                    <div style={{ width: 54, flex: 'none', textAlign: 'center' }}>
                      <div style={{ ...mono(), fontSize: 16, fontWeight: 700, color: c.over ? C.red : C.ink, lineHeight: 1.1 }}>{c.n}</div>
                      <div style={{ fontSize: 9.6, textTransform: 'uppercase', letterSpacing: '.05em', color: c.over ? C.red : C.muted }}>{c.unit}</div>
                    </div>
                    <div style={{ width: 3, alignSelf: 'stretch', borderRadius: 3, background: c.over ? C.red : C.accent }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
                        <b style={{ fontSize: 14, color: C.ink, fontWeight: 600 }}>{d.title}</b>
                        {d.note === 'auto:lease' && <span style={autoBadge}>Auto</span>}
                        <LeadChips leadDays={d.leadDays} />
                      </div>
                      <small style={{ fontSize: 12, color: C.muted }}>{typeLabel(d.type)}{scope ? ` · ${scope}` : ''}{d.dueDate ? ` · ${fmtDate(d.dueDate)}` : ''}{d.assignee ? ` · ${d.assignee}` : ''}</small>
                    </div>
                    <button onClick={() => setStatus(d.id, 'dismissed')} disabled={busyId === d.id} aria-label="Dismiss" title="Dismiss" style={{ ...ghostBtn, padding: '7px 10px', color: C.muted, opacity: busyId === d.id ? 0.6 : 1 }}>
                      Dismiss
                    </button>
                    <button onClick={() => setStatus(d.id, 'done')} disabled={busyId === d.id} aria-label="Mark done" style={{ ...ghostBtn, padding: '7px 12px', opacity: busyId === d.id ? 0.6 : 1 }}>
                      <Icon n="check" size={14} /> Done
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {done.length > 0 && (
            <div>
              <div style={{ fontSize: 11.5, fontWeight: 600, color: C.muted, textTransform: 'uppercase', letterSpacing: '.05em', margin: '2px 2px 8px' }}>Completed</div>
              <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 13, overflow: 'hidden' }}>
                {done.map((d, i) => (
                  <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '11px 16px', borderTop: i ? `1px solid ${C.line2}` : 0 }}>
                    <span style={{ width: 26, height: 26, borderRadius: 999, background: C.greenSoft, color: C.green, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}><Icon n="check" size={14} /></span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <b style={{ fontSize: 13.3, color: C.ink2, fontWeight: 600, display: 'block' }}>{d.title}</b>
                      <small style={{ fontSize: 11.8, color: C.muted }}>{typeLabel(d.type)}{d.dueDate ? ` · ${fmtDate(d.dueDate)}` : ''}</small>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function AddPanel({
  propertyId,
  tenancies,
  onDone,
  notify,
}: {
  propertyId?: string;
  tenancies: TenancyRow[];
  onDone: () => void;
  notify: (s: string) => void;
}) {
  const [type, setType] = useState<(typeof DATE_TYPES)[number]>('renewal');
  const [title, setTitle] = useState('');
  const [tenancyId, setTenancyId] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [leadDays, setLeadDays] = useState('30,7');
  const [assignee, setAssignee] = useState('');
  const [busy, setBusy] = useState(false);

  const scopeOk = !!tenancyId || !!propertyId;
  const canSubmit = !!title.trim() && !!dueDate && scopeOk && !busy;

  async function submit() {
    if (!title.trim() || !dueDate) return;
    if (!scopeOk) {
      notify('Pick a tenancy for this reminder.');
      return;
    }
    const epoch = Date.parse(`${dueDate}T00:00:00Z`);
    if (Number.isNaN(epoch)) {
      notify('Enter a valid due date.');
      return;
    }
    setBusy(true);
    try {
      await api.createCriticalDate({
        type,
        title: title.trim(),
        ...(tenancyId ? { tenancyId } : { propertyId }),
        dueDate: epoch,
        anchor: 'fixed',
        leadDays: leadDays.trim() || undefined,
        assignee: assignee.trim() || undefined,
      });
      notify('Reminder added');
      onDone();
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'Could not add reminder');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 13, padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <label style={fieldLabel}>Title
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Fire-insurance renewal" style={input} />
      </label>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <label style={fieldLabel}>Type
          <select value={type} onChange={(e) => setType(e.target.value as (typeof DATE_TYPES)[number])} style={input}>
            {DATE_TYPES.map((t) => (<option key={t} value={t}>{typeLabel(t)}</option>))}
          </select>
        </label>
        <label style={fieldLabel}>For
          <select value={tenancyId} onChange={(e) => setTenancyId(e.target.value)} style={input}>
            <option value="">Property (all)</option>
            {tenancies.map((t) => (<option key={t.id} value={t.id}>{t.unitLabel} · {t.tenantName}</option>))}
          </select>
        </label>
        <label style={fieldLabel}>Due date
          <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} style={input} />
        </label>
        <label style={fieldLabel}>Remind (days before)
          <input value={leadDays} onChange={(e) => setLeadDays(e.target.value)} placeholder="30,7" style={input} />
        </label>
        <label style={fieldLabel}>Assign to (optional)
          <input value={assignee} onChange={(e) => setAssignee(e.target.value)} placeholder="e.g. Manager, CA" style={input} />
        </label>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button onClick={submit} disabled={!canSubmit} style={{ ...primaryBtn, opacity: canSubmit ? 1 : 0.55, cursor: canSubmit ? 'pointer' : 'default' }}>
          {busy ? 'Adding…' : 'Add reminder'}
        </button>
      </div>
    </div>
  );
}

const primaryBtn: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 7,
  background: C.accent,
  color: '#fff',
  border: 0,
  borderRadius: 9,
  padding: '9px 14px',
  fontWeight: 600,
  fontSize: 13.3,
  cursor: 'pointer',
};

const autoBadge: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 700,
  color: C.accent2,
  background: C.soft,
  borderRadius: 999,
  padding: '2px 7px',
  textTransform: 'uppercase',
  letterSpacing: '.04em',
};

const ghostBtn: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  background: C.card,
  color: C.accent,
  border: `1px solid ${C.line}`,
  borderRadius: 9,
  padding: '9px 14px',
  fontWeight: 600,
  fontSize: 12.8,
  cursor: 'pointer',
};

const fieldLabel: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 5,
  fontSize: 12,
  fontWeight: 600,
  color: C.muted,
  textTransform: 'uppercase',
  letterSpacing: '.04em',
};

const input: React.CSSProperties = {
  border: `1px solid ${C.line}`,
  borderRadius: 9,
  padding: '9px 11px',
  fontSize: 13.5,
  color: C.ink,
  background: '#fff',
  fontFamily: 'inherit',
  textTransform: 'none',
  letterSpacing: 'normal',
  fontWeight: 400,
};

const retryBtn: React.CSSProperties = {
  marginTop: 4,
  border: `1px solid ${C.line}`,
  background: C.card,
  color: C.accent,
  fontWeight: 600,
  fontSize: 13,
  padding: '8px 16px',
  borderRadius: 9,
  cursor: 'pointer',
};
